// ============================================
// APPLICATION - Caja Hooks
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cajaRepository } from '../../infrastructure/http/caja.repository';
import { queryKeys } from '../query-keys';
import type { AperturaCajaDto, CierreCajaDto, FiltrosCuadraturaCaja } from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';

/**
 * Hook para abrir caja
 */
export const useAbrirCaja = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AperturaCajaDto) => cajaRepository.abrirCaja(dto),
    onSuccess: () => {
      // Invalidar queries relacionadas si existen
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.cuadraturas() });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para cerrar caja
 */
export const useCerrarCaja = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CierreCajaDto) => cajaRepository.cerrarCaja(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.cuadraturas() });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para obtener cuadraturas de caja
 */
export const useCuadraturasCaja = () => {
  return useQuery({
    queryKey: queryKeys.caja.cuadraturas(),
    queryFn: () => cajaRepository.obtenerCuadraturas(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obtener vendedores de caja
 */
export const useVendedoresCaja = () => {
  return useQuery({
    queryKey: queryKeys.caja.vendedores(),
    queryFn: () => cajaRepository.obtenerVendedoresCaja(),
    staleTime: 10 * 60 * 1000, // 10 minutos (lista relativamente estática)
  });
};

/**
 * Hook para obtener cuadraturas abiertas
 */
export const useCuadraturasAbiertas = () => {
  return useQuery({
    queryKey: queryKeys.caja.cuadraturasAbiertas(),
    queryFn: () => cajaRepository.obtenerCuadraturasAbiertas(),
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
};

/**
 * Hook para obtener cuadraturas con filtros
 */
export const useCuadraturasConFiltros = (filtros: FiltrosCuadraturaCaja, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.caja.cuadraturasConFiltros(filtros),
    queryFn: () => cajaRepository.obtenerCuadraturasConFiltros(filtros),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
};

/**
 * Hook para cerrar caja por ID
 */
export const useCerrarCajaById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cajaId: number) => cajaRepository.cerrarCajaById(cajaId),
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para obtener mi status de caja
 */
export const useObtenerMiStatusCaja = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['caja', 'mi-status'],
    queryFn: () => cajaRepository.obtenerMiStatus(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
